import type { FC } from 'react';
import { useRef, useState, useContext } from 'react';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import { useResizeObserver } from '@wix/da-hooks/pkg/useResizeObserver';
import Slider from '@wix/da-ds/pkg/Slider';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import PostCompactCard from '@wix/da-shared-react/pkg/PostCompactCard';
import { DeviationLink } from '@wix/da-shared-react/pkg/Duperbrowse';
import SwipeSlider from '@wix/da-shared-react/pkg/SwipeSlider';
import s from './PostsSlider.scss';

export interface Props {
  posts: PapiDeviation[];
  profileOwnerUsername: string;
  itemsPerFetch: number;
  className?: string;
  onPostClick?: (post: PapiDeviation) => void;
}

const RESIZE_THROTTLE_DELAY = 300;
const RESIZE_THROTTLE_OPTIONS = { trailing: true };

const PostsSlider: FC<Props> = ({ posts, className }) => {
  const isMobile = useContext(MobileContext);

  const sliderRef = useRef<HTMLDivElement>(null);
  const [sliderWidth, setSliderWidth] = useState(sliderRef.current?.getBoundingClientRect().width);
  const [sliderHeight, setSliderHeight] = useState(
    sliderRef.current?.getBoundingClientRect().height
  );

  const handleResize = useThrottle(
    () => {
      if (!sliderRef.current) return;
      const newWidth = sliderRef.current.getBoundingClientRect().width;
      if (newWidth !== sliderWidth) {
        setSliderWidth(newWidth);
      }
      const newHeight = sliderRef.current.getBoundingClientRect().height;
      if (newHeight !== sliderHeight) {
        setSliderHeight(newHeight);
      }
    },
    [sliderWidth, sliderHeight],
    RESIZE_THROTTLE_DELAY,
    RESIZE_THROTTLE_OPTIONS
  );
  useResizeObserver(sliderRef, handleResize);

  if (!posts.length) {
    return null;
  }

  const sliderItems = posts.map(post => (
    <div key={post.deviationId} className={s['post-card']}>
      <DeviationLink deviation={post} title={post.title}>
        <PostCompactCard deviation={post} className={s['post-compact-card']} />
      </DeviationLink>
    </div>
  ));

  return (
    <div className={classnames(s['root'], className)} ref={sliderRef}>
      {isMobile ? (
        <SwipeSlider className={s['slider']} indicatorsClassName={s['indicators']} maxItems={10}>
          {sliderItems}
        </SwipeSlider>
      ) : (
        <Slider className={s['slider']} contentClassName={s['slider-content']}>
          {sliderItems}
        </Slider>
      )}
    </div>
  );
};

PostsSlider.displayName = 'PostsSlider';

export default PostsSlider;
